
/**
 * SAB-932: which players the draft pool shows right now.
 *
 * ── THE TENSION THIS RESOLVES ───────────────────────────────────────────────
 *
 * Two tickets, three days apart, both correct, pulling opposite ways:
 *
 *   SAB-843  the pool used to drop drafted players outright. Mid-search a player you were
 *            hunting simply VANISHED, which is indistinguishable from a broken filter at
 *            the moment it matters most. So the filter was removed and drafted rows are
 *            now dimmed, struck through and labelled "Drafted by X".
 *
 *   SAB-932  but in a 190-pick draft the top of the list then fills with players nobody
 *            can pick, and it degrades every round. By the late rounds most of what a
 *            manager scrolls past on a running clock is dead.
 *
 * They are about different ACTIVITIES, which is what makes them reconcilable:
 *
 *   BROWSING  -> hide drafted. This is the 95% case and what every other platform does
 *                (Sleeper removes them; ESPN's "Show drafted" checkbox is off by default;
 *                Yahoo and NFL.com drop them from the available list).
 *   SEARCHING -> ALWAYS show drafted, marked. This is SAB-843's entire fix, and it is why
 *                `hideDrafted` cannot be a simple unconditional filter.
 *
 * So: a drafted player is hidden only when `hideDrafted` is on AND the user is not
 * searching. Never on a query. That is the whole rule, and it is the reason this is a
 * shared pure function rather than a `.filter()` at two call sites -- during the SAB-931
 * transition both rooms must behave identically, and the only way to guarantee that is for
 * both to call the same code.
 *
 * ── A POSITION FILTER IS BROWSING, NOT SEARCHING ────────────────────────────
 *
 * Narrowing to RB is a browse, so drafted RBs stay hidden. Only a text query lifts the
 * hide. Treating the position filter as a search would hand back exactly the complaint
 * SAB-932 was filed about, one position at a time.
 *
 * ── A KNOWN, DELIBERATE ROUGH EDGE ──────────────────────────────────────────
 *
 * The query matches name, position OR team alias (SAB-457 item 7, preserved from the
 * legacy room). So typing "RB" is a text query and therefore lifts the hide, showing
 * drafted RBs -- browsing-by-typing gets search semantics.
 *
 * Left as-is deliberately. The alternative -- lift the hide only when the query matches a
 * NAME -- is more correct in that one case and less predictable in general: two rows
 * matching the same query would then be included on different grounds. The failure mode
 * that must never happen is SAB-843's (a hunted player silently absent), and "any query
 * shows everything matching" is the rule that cannot produce it. `hiddenDraftedCount` is
 * returned so the UI can always say how many rows the hide is holding back.
 */

/** Mirrors the legacy room's POS_FILTERS, including the IDP grouping. */
export const POOL_POSITION_FILTERS = [
  'ALL', 'QB', 'RB', 'WR', 'TE', 'K', 'P', 'DST', 'COACH', 'IDP',
] as const;

/**
 * SAB-998. Deliberately NOT a member of POOL_POSITION_FILTERS.
 *
 * That array is iterated to build the per-league tab list, so adding FLEX to it would
 * offer the tab to every league and then require a special case to remove it again. FLEX
 * is appended by `poolPositionFiltersFor` only for leagues that declare a FLEX slot, so
 * the default stays "not offered" and the exception is the thing that has to be earned.
 */
export const FLEX_POOL_FILTER = 'FLEX' as const;

export type PoolPositionFilter = (typeof POOL_POSITION_FILTERS)[number] | typeof FLEX_POOL_FILTER;

/**
 * SAB-955: the tabs this league should show.
 *
 * A tab whose position can never start here would return an empty list on every click, so
 * it is not offered. IDP is a GROUP, so its tab survives if ANY of its positions is
 * startable -- a league with IDP slots keeps the tab even though no single position named
 * "IDP" exists.
 *
 * `null` (not loaded) returns the full list rather than a partial one, so the row never
 * flickers down and back up.
 */
export function poolPositionFiltersFor(
  startable: ReadonlySet<string> | null,
  flex?: { hasFlex: boolean; flex: ReadonlySet<string> | null; idp?: ReadonlySet<string> | null },
): PoolPositionFilter[] {
  // SAB-812: the IDP tab is withheld until the eligibility rows land, because a tab that
  // cannot yet answer "which positions are IDP" would render an EMPTY pool on a running
  // clock -- the failure this module guards against everywhere. FLEX is withheld for the
  // same reason a few lines below; IDP simply needed the same treatment once its set
  // stopped being a compile-time constant.
  const idp = flex?.idp ?? null;
  const base: PoolPositionFilter[] =
    startable === null
      ? POOL_POSITION_FILTERS.filter((f) => f !== 'IDP' || idp !== null)
      : POOL_POSITION_FILTERS.filter((f) => {
          if (f === 'ALL') return true;
          if (f === 'IDP') return idp !== null && [...startable].some((p) => idp.has(p));
          return startable.has(f);
        });

  // SAB-998: FLEX is a slot, so its tab is offered only to leagues that declare one, and
  // only once the eligible position set has loaded -- a tab that cannot yet filter would
  // render an empty pool on a running clock, which is the failure this file guards against
  // everywhere else. Absent by default, so callers that pass nothing behave exactly as before.
  if (flex?.hasFlex && flex.flex !== null) return [...base, 'FLEX'];
  return base;
}

/**
 * Positions a FLEX tab stands for, or null when it is not the FLEX tab.
 *
 * Kept beside the filter so the pool's "which rows does this tab show" logic has one
 * answer. FLEX matches a SET; every other tab matches one position or the IDP group.
 */
export function flexTabPositions(
  posFilter: PoolPositionFilter | undefined,
  flex: ReadonlySet<string> | null,
): ReadonlySet<string> | null {
  return posFilter === 'FLEX' ? flex : null;
}

/** The minimum a row needs to be filtered. Both rooms' shapes satisfy it. */
export interface PoolPlayerLike {
  id: string;
  full_name: string;
  position: string;
  /** Team alias, e.g. "KC". Searchable, per SAB-457 item 7. */
  nfl_team?: string | null;
}

export interface FilterDraftPoolArgs<T extends PoolPlayerLike> {
  players: T[];
  /** Player ids already drafted IN THIS LEAGUE. Derived from `picks`, never fetched. */
  draftedIds: ReadonlySet<string>;
  query?: string;
  posFilter?: PoolPositionFilter;
  /** The user's preference. Ignored while searching -- see the header. */
  hideDrafted?: boolean;

  /**
   * How to read a row's team alias. Defaults to `p.nfl_team`, which is V2's shape; the
   * legacy room carries it as `franchise.alias`. An accessor rather than a required field
   * so neither room has to re-map its rows into new objects just to be filtered -- that
   * would break object identity for everything downstream of the filter.
   */
  teamAliasOf?: (p: T) => string | null | undefined;

  /**
   * SAB-998: the positions this league's FLEX slot accepts, from `useLeagueRosterability`.
   * Required only when `posFilter` is 'FLEX'; ignored otherwise.
   */
  flexPositions?: ReadonlySet<string> | null;

  /**
   * SAB-812: the positions that map to an IDP slot, from `useLeagueRosterability`.
   * Required only when `posFilter` is 'IDP'; ignored otherwise.
   */
  idpPositions?: ReadonlySet<string> | null;
}

export interface FilterDraftPoolResult<T> {
  rows: T[];
  /** True when a non-empty query is in force, i.e. the hide is lifted. */
  searching: boolean;
  /** How many rows the hide is currently withholding. 0 whenever `searching`. */
  hiddenDraftedCount: number;
  /** Rows matching position + query BEFORE the drafted hide. */
  matchedCount: number;
}

/** Does this row satisfy the position filter? */
function matchesPosition(
  position: string,
  posFilter: PoolPositionFilter,
  flex: ReadonlySet<string> | null,
  idp: ReadonlySet<string> | null,
): boolean {
  if (posFilter === 'ALL') return true;
  // SAB-806: IDP is a GROUP, not a position. SAB-812: the set now comes from
  // nfl_slot_eligibility via the caller, not from a constant. The old constant was correct
  // when this was written and had ALREADY been wrong once -- it omitted SAF/OLB/NT/MLB
  // while listing S/SS, hiding 332 players behind this very tab.
  //
  // `null` matches nothing, exactly as FLEX does below, and for the same reason: the tab is
  // not offered until the set has loaded (see poolPositionFiltersFor), so reaching here
  // with null means the caller wired it wrong and an empty pool is the visible failure.
  if (posFilter === 'IDP') return idp !== null && idp.has(position);
  // SAB-998: FLEX is a SLOT. Its set comes from nfl_slot_eligibility via the caller rather
  // than from a constant here, because a constant would be a second copy of a rule the
  // server owns and would drift silently the day the eligibility table changes.
  //
  // `flex === null` (not loaded) matches NOTHING rather than everything. That is the
  // opposite of the "null means do not filter" stance taken elsewhere in this module, and
  // deliberately so: the tab is only ever rendered once the set has loaded, so reaching
  // here with null means the caller wired it wrong. Falling back to "match everything"
  // would hide that by showing a plausible full pool under a FLEX tab -- the wrong players,
  // with nothing to notice. Empty is the visible failure.
  if (posFilter === 'FLEX') return flex !== null && flex.has(position);
  return position === posFilter;
}

/** Substring match on name, position or team alias — SAB-457 item 7, kept verbatim. */
function matchesQuery<T extends PoolPlayerLike>(
  p: T, q: string, teamAliasOf: (p: T) => string | null | undefined,
): boolean {
  return p.full_name.toLowerCase().includes(q)
    || p.position.toLowerCase().includes(q)
    || (teamAliasOf(p)?.toLowerCase().includes(q) ?? false);
}

export function filterDraftPool<T extends PoolPlayerLike>(
  args: FilterDraftPoolArgs<T>,
): FilterDraftPoolResult<T> {
  const { players, draftedIds, hideDrafted = true } = args;
  const posFilter = args.posFilter ?? 'ALL';
  const teamAliasOf = args.teamAliasOf ?? ((p: T) => p.nfl_team);
  const q = (args.query ?? '').trim().toLowerCase();
  const searching = q.length > 0;

  const matched = players.filter(
    p => matchesPosition(p.position, posFilter, args.flexPositions ?? null, args.idpPositions ?? null)
      && (!searching || matchesQuery(p, q, teamAliasOf)),
  );

  // THE RULE. A query always wins over the preference; nothing a user is hunting for may
  // be silently absent.
  const hiding = hideDrafted && !searching;
  const rows = hiding ? matched.filter(p => !draftedIds.has(p.id)) : matched;

  return {
    rows,
    searching,
    hiddenDraftedCount: hiding ? matched.length - rows.length : 0,
    matchedCount: matched.length,
  };
}
